import { useOperatorsClientsAllQuery } from '@/api/queries/operators';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Typography } from '@mui/material';
import {
  corporateCharterClientDetailsSchema,
  CorporateCharterClientDetailsSchemaType,
} from 'components/forms/CorporateCharter/types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutocompleteField, colors, ControlledField, SelectOption } from 'remote/global';

interface Props {
  onBack: (data: CorporateCharterClientDetailsSchemaType) => void;
  onValid: SubmitHandler<CorporateCharterClientDetailsSchemaType>;
  defaultValues?: CorporateCharterClientDetailsSchemaType;
}

function ClientDetailsForm({ defaultValues, onValid, onBack }: Props) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { data: approvedClients } = useOperatorsClientsAllQuery();

  const modifiedClientList: SelectOption[] =
    approvedClients?.map((client) => ({
      label: client.value,
      value: client.id,
    })) ?? [];

  const formInstance = useForm<CorporateCharterClientDetailsSchemaType>({
    defaultValues: {
      clientId: '' as any,
      ...defaultValues,
    },
    resolver: zodResolver(corporateCharterClientDetailsSchema),
  });
  const { clearErrors, control, setValue, handleSubmit, getValues } = formInstance;
  const { isSubmitting } = formInstance.formState;

  return (
    <Box
      p='0 24px 24px'
      overflow='auto'
      sx={{
        width: '100%',
        margin: '0 auto',
      }}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <Box
          display='flex'
          justifyContent='flex-start'
          sx={{
            width: '100%',
            flexDirection: 'column',
          }}
        >
          <Box
            mr='48px'
            width='100%'
            maxWidth={{
              xs: '100%',
              sm: '370px',
            }}
          >
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontSize: '18px',
                mb: '24px',
                fw: 600,
              }}
            >
              {t('CorporateCharterView.steps.clientDetails.company.title')}
            </Typography>

            {!modifiedClientList?.length ? null : (
              <ControlledField
                control={control}
                name='clientId'
                render={(renderProps) => (
                  <AutocompleteField
                    {...renderProps}
                    field={{
                      ...renderProps.field,
                    }}
                    options={modifiedClientList}
                    fieldProps={{
                      onChange: (_, newValue: any) => {
                        if (!newValue) return;

                        const currentSelectedSupplier = approvedClients?.find(
                          (client) => client.id === newValue.value
                        );
                        if (!currentSelectedSupplier) return;

                        clearErrors('clientId');
                        setValue('clientId', newValue.value);
                      },
                      disableClearable: false,
                    }}
                  />
                )}
              />
            )}
          </Box>

          <Box
            display='flex'
            alignItems='center'
            width='100%'
            m='24px 0'
            justifyContent='space-between'
            flexDirection={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Button
              sx={{
                mr: {
                  xs: 0,
                  sm: '24px',
                },
                mb: {
                  xs: '12px',
                  sm: 0,
                },
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
              }}
              variant='outlined'
              onClick={() => onBack(getValues())}
            >
              {t('back')}
            </Button>
            <Button
              disabled={isSubmitting}
              type='submit'
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
              }}
            >
              {t('confirm')}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
}

export default ClientDetailsForm;
